import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SystemSettings = () => {
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [lastTestResult, setLastTestResult] = useState(null);
  const [lastTestTime, setLastTestTime] = useState(null);
  const [settings, setSettings] = useState({
    is_active: false,
    ga4_service_account_email: '',
    ga4_credentials_json: '',
    use_mock_data: true
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/integrations/ga4/global-settings`);
      setSettings(response.data);
    } catch (error) {
      // Se não existe configuração ainda, manter valores padrão
      if (error.response?.status !== 404) {
        toast.error('Erro ao carregar configurações');
      }
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/integrations/ga4/global-settings`, settings);
      toast.success('Integração global com o GA4 salva com sucesso.');
      await loadSettings();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erro ao salvar configurações';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setLastTestResult(null);
    try {
      const response = await axios.post(`${API_URL}/api/integrations/ga4/test-connection`);
      
      if (response.data.ok) {
        const now = new Date();
        setLastTestTime(now);
        setLastTestResult({ success: true, mode: response.data.mode });
        
        if (response.data.mode === 'mock') {
          toast.success('Teste de conexão concluído em modo simulado (mock).');
        } else {
          toast.success('Conexão com o GA4 verificada com sucesso.');
        }
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erro ao testar conexão';
      setLastTestResult({ success: false, error: errorMsg });
      toast.error(errorMsg);
    } finally {
      setTesting(false);
    }
  };
  
  const getDataModeLabel = () => {
    if (settings.use_mock_data) {
      return 'Modo de dados: simulados (mock)';
    }
    return 'Modo de dados: reais (GA4)';
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Configurações do Sistema</h1>
        <p className="text-sm text-muted-foreground">Gerencie as integrações globais da aplicação</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Integração global com GA4</CardTitle>
          <CardDescription>
            Configure aqui a integração global com o Google Analytics 4. 
            Todos os projetos poderão usar essa conexão.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/50">
            <div className="space-y-0.5">
              <Label className="text-base">Ativar integração global com o GA4</Label>
              <p className="text-sm text-muted-foreground">
                Permite que projetos utilizem métricas do Google Analytics 4
              </p>
            </div>
            <Switch
              checked={settings.is_active}
              onCheckedChange={(checked) => setSettings({ ...settings, is_active: checked })}
            />
          </div>

          {settings.is_active && (
            <>
              <div className="space-y-2">
                <Label htmlFor="email">E-mail da conta de serviço (opcional)</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="exemplo@projeto.iam.gserviceaccount.com"
                  value={settings.ga4_service_account_email}
                  onChange={(e) => setSettings({ ...settings, ga4_service_account_email: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  E-mail da conta de serviço do Google Cloud para autenticação
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="credentials">Credenciais do GA4 (JSON ou chave da API)</Label>
                <Textarea
                  id="credentials"
                  placeholder='{"type": "service_account", "project_id": "...", ...}'
                  rows={6}
                  value={settings.ga4_credentials_json}
                  onChange={(e) => setSettings({ ...settings, ga4_credentials_json: e.target.value })}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Cole aqui o conteúdo do arquivo JSON de credenciais da conta de serviço
                </p>
              </div>

              <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/50">
                <div className="space-y-0.5">
                  <Label className="text-base">Usar dados simulados (mock)</Label>
                  <p className="text-sm text-muted-foreground">
                    Ative para usar dados mockados ao invés de dados reais do GA4
                  </p>
                </div>
                <Switch
                  checked={settings.use_mock_data}
                  onCheckedChange={(checked) => setSettings({ ...settings, use_mock_data: checked })}
                />
              </div>

              <div className="p-3 rounded-lg bg-slate-100 border border-slate-200">
                <p className="text-sm font-medium text-slate-700">{getDataModeLabel()}</p>
              </div>
            </>
          )}

          <div className="flex gap-3">
            <Button 
              onClick={handleSave} 
              disabled={loading}
              className="flex-1"
            >
              {loading ? 'Salvando...' : 'Salvar configurações'}
            </Button>
            <Button 
              onClick={handleTestConnection} 
              disabled={testing || !settings.is_active}
              variant="outline"
            >
              {testing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Testando conexão...
                </>
              ) : (
                'Testar conexão'
              )}
            </Button>
          </div>

          {/* Test connection status */}
          {lastTestResult && (
            <div className={`flex items-start gap-2 p-4 rounded-lg border ${
              lastTestResult.success 
                ? 'bg-green-50 border-green-200' 
                : 'bg-red-50 border-red-200'
            }`}>
              {lastTestResult.success ? (
                <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
              )}
              <div className="flex-1">
                <p className={`text-sm font-medium ${
                  lastTestResult.success ? 'text-green-900' : 'text-red-900'
                }`}>
                  {lastTestResult.success 
                    ? `Teste bem-sucedido (modo: ${lastTestResult.mode === 'mock' ? 'simulado' : 'real'})` 
                    : 'Falha no teste de conexão'}
                </p>
                {lastTestResult.success && lastTestTime && (
                  <p className="text-xs text-green-700 mt-1">
                    Último teste: {lastTestTime.toLocaleString('pt-BR')}
                  </p>
                )}
                {!lastTestResult.success && (
                  <p className="text-xs text-red-700 mt-1">
                    {lastTestResult.error}
                  </p>
                )}
              </div>
            </div>
          )}

          {settings.is_active && (
            <div className="flex items-start gap-2 p-4 rounded-lg bg-blue-50 border border-blue-200">
              <CheckCircle2 className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-900">Integração global ativa</p>
                <p className="text-xs text-blue-700 mt-1">
                  Agora você pode configurar propriedades do GA4 individuais para cada projeto na página Relatórios.
                </p>
              </div>
            </div>
          )}

          {!settings.is_active && (
            <div className="flex items-start gap-2 p-4 rounded-lg bg-slate-50 border border-slate-200">
              <AlertCircle className="h-5 w-5 text-slate-600 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-900">Integração desativada</p>
                <p className="text-xs text-slate-700 mt-1">
                  Ative a integração global para permitir que os projetos utilizem dados do GA4.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
